// src/pages/HomePage.tsx
// v3.20 homepage — product-led, "code learners" positioning, cube/smiley brand.
// Replaces the geo-routed landing page at `/`. The four geo pages (/india,
// /us, /gcc, /latam) stay live for ads and SEO.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const LinkedInIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="#0A66C2">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const InstagramIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24">
    <defs>
      <radialGradient id="igGradientHome" cx="30%" cy="107%" r="150%">
        <stop offset="0%" stopColor="#fdf497" />
        <stop offset="5%" stopColor="#fdf497" />
        <stop offset="45%" stopColor="#fd5949" />
        <stop offset="60%" stopColor="#d6249f" />
        <stop offset="90%" stopColor="#285AEB" />
      </radialGradient>
    </defs>
    <path fill="url(#igGradientHome)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [running, setRunning] = useState<'idle' | 'running' | 'done'>('idle');
  const [runtime, setRuntime] = useState<number | null>(null);
  const runStartRef = useRef<number>(0);

  // Reveal-on-scroll
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('home-reveal-show');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.15 });
    document.querySelectorAll('section[data-reveal]').forEach(s => io.observe(s));
    return () => io.disconnect();
  }, []);

  const runDemo = () => {
    if (running !== 'idle') return;
    setRunning('running');
    runStartRef.current = performance.now();
    setTimeout(() => {
      setRuntime(Math.round(performance.now() - runStartRef.current));
      setRunning('done');
      setTimeout(() => setRunning('idle'), 1200);
    }, 750);
  };

  const goPay = () => navigate('/pay');
  const goLogin = () => navigate('/login');
  const goPricing = () => navigate('/pricing');

  return (
    <div className="home-root">
      <style>{`
        .home-root {
          --bg-cream: #fefae8;
          --bg-cream-2: #fff5d1;
          --bg-card: #ffffff;
          --ink: #1a1a2e;
          --ink-soft: #5a5a72;
          --ink-mute: #8a8aa3;
          --brand-purple: #7c7cff;
          --brand-pink: #d166e8;
          --brand-gradient: linear-gradient(135deg, #7c7cff 0%, #a065f0 50%, #d166e8 100%);
          --border-soft: #1a1a2e15;
          --code-bg: #1a1525;
          --code-text: #e8e3f0;
          font-family: 'Inter', system-ui, sans-serif;
          background: var(--bg-cream);
          color: var(--ink);
          -webkit-font-smoothing: antialiased;
          min-height: 100vh;
        }
        .home-root .mono { font-family: 'JetBrains Mono', monospace; }
        .home-root .brand-text {
          background: var(--brand-gradient);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        .home-root .brand-bg { background: var(--brand-gradient); }
        .home-root .card-soft {
          background: var(--bg-card);
          border: 1.5px solid var(--border-soft);
          border-radius: 20px;
          box-shadow: 0 1px 0 rgba(26,26,46,.04), 0 8px 24px -10px rgba(26,26,46,.08);
          transition: transform .3s cubic-bezier(.16,1,.3,1), box-shadow .3s, border-color .3s;
        }
        .home-root .card-soft:hover {
          transform: translateY(-3px);
          box-shadow: 0 1px 0 rgba(26,26,46,.04), 0 16px 32px -12px rgba(124,124,255,.18);
          border-color: #7c7cff40;
        }
        .home-root .code-block {
          background: var(--code-bg);
          color: var(--code-text);
          border-radius: 12px;
          padding: 16px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 12px;
          line-height: 1.7;
          box-shadow: 0 8px 32px -8px rgba(26,21,37,.4);
          white-space: pre;
        }
        .home-root .kw { color: #c4b5fd; }
        .home-root .str { color: #6ee7b7; }
        .home-root .com { color: #8a8aa3; }
        .home-root .fn { color: #93c5fd; }
        @keyframes home-float-1 { 0%,100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-14px) rotate(2deg); } }
        @keyframes home-float-2 { 0%,100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-10px) rotate(-3deg); } }
        @keyframes home-float-3 { 0%,100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-18px) rotate(4deg); } }
        .home-root .float-1 { animation: home-float-1 7s ease-in-out infinite; }
        .home-root .float-2 { animation: home-float-2 9s ease-in-out infinite; }
        .home-root .float-3 { animation: home-float-3 6s ease-in-out infinite; }
        @keyframes home-blink-eyes { 0%,92%,100% { transform: scaleY(1); } 94%,98% { transform: scaleY(0.1); } }
        .home-root .blink { animation: home-blink-eyes 5s infinite; transform-origin: center; transform-box: fill-box; }
        @keyframes home-spin { to { transform: rotate(360deg); } }
        .home-root .home-spin { animation: home-spin 1s linear infinite; display: inline-block; }
        .home-root .dots {
          background-image: radial-gradient(circle, #1a1a2e15 1px, transparent 1px);
          background-size: 28px 28px;
        }
        .home-root .num-cube {
          background: var(--brand-gradient);
          color: white;
          font-weight: 800;
          box-shadow: 0 8px 24px -8px #7c7cff80;
        }
        .home-root section[data-reveal] {
          opacity: 0; transform: translateY(20px);
          transition: opacity .7s cubic-bezier(.16,1,.3,1), transform .7s cubic-bezier(.16,1,.3,1);
        }
        .home-root section.home-reveal-show {
          opacity: 1; transform: translateY(0);
        }
        .home-root .tape {
          background: linear-gradient(135deg, #fff5d1, #fef9e0);
          border: 1px dashed #1a1a2e25;
        }
      `}</style>

      {/* SVG symbol definitions (cube + smiley mascot) */}
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <symbol id="iso-cube" viewBox="0 0 100 100">
            <polygon points="50,5 95,30 50,55 5,30" fill="#d166e8" />
            <polygon points="5,30 5,75 50,100 50,55" fill="#7c7cff" />
            <polygon points="95,30 95,75 50,100 50,55" fill="#a065f0" />
          </symbol>
          <symbol id="iso-cube-soft" viewBox="0 0 100 100">
            <polygon points="50,5 95,30 50,55 5,30" fill="#f0d1ff" />
            <polygon points="5,30 5,75 50,100 50,55" fill="#c5c5ff" />
            <polygon points="95,30 95,75 50,100 50,55" fill="#dab5fa" />
          </symbol>
          <symbol id="cube-smiley" viewBox="0 0 100 100">
            <rect x="6" y="6" width="88" height="88" rx="22" fill="url(#smileyGrad)" />
            <circle className="blink" cx="36" cy="42" r="6" fill="#fff" />
            <circle className="blink" cx="64" cy="42" r="6" fill="#fff" />
            <path d="M 32 62 Q 50 78 68 62" stroke="#fff" strokeWidth="4" strokeLinecap="round" fill="none" />
          </symbol>
          <linearGradient id="smileyGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#7c7cff" />
            <stop offset="1" stopColor="#d166e8" />
          </linearGradient>
        </defs>
      </svg>

      {/* ───── NAV ───── */}
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-md border-b" style={{ background: 'rgba(254,250,232,0.85)', borderColor: '#1a1a2e14' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <svg className="w-8 h-8"><use href="#cube-smiley" /></svg>
            <span className="font-bold tracking-tight text-lg">Cubemate</span>
          </a>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium" style={{ color: 'var(--ink-soft)' }}>
            <a href="#product" className="hover:text-[#1a1a2e] transition">Product</a>
            <a href="#outcomes" className="hover:text-[#1a1a2e] transition">Stories</a>
            <a href="#how" className="hover:text-[#1a1a2e] transition">How it works</a>
            <button onClick={goPricing} className="hover:text-[#1a1a2e] transition">Pricing</button>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={goLogin} className="hidden sm:inline text-sm font-medium" style={{ color: 'var(--ink-soft)' }}>Sign in</button>
            <button onClick={goPay} className="px-4 py-2 text-sm font-semibold rounded-full brand-bg text-white hover:opacity-90 transition shadow-md">Get started</button>
          </div>
        </div>
      </nav>

      {/* ───── HERO ───── */}
      <header className="relative pt-32 pb-20 overflow-hidden">
        <svg className="absolute top-28 left-[6%] w-16 h-16 opacity-80 float-1"><use href="#iso-cube" /></svg>
        <svg className="absolute top-44 right-[8%] w-20 h-20 opacity-90 float-2"><use href="#iso-cube-soft" /></svg>
        <svg className="absolute top-72 left-[15%] w-10 h-10 opacity-70 float-3"><use href="#iso-cube" /></svg>
        <svg className="absolute top-96 right-[18%] w-12 h-12 opacity-60 float-1"><use href="#iso-cube-soft" /></svg>
        <div
          className="absolute inset-0 dots opacity-50 pointer-events-none"
          style={{
            maskImage: 'radial-gradient(ellipse 70% 50% at 50% 30%, black, transparent)',
            WebkitMaskImage: 'radial-gradient(ellipse 70% 50% at 50% 30%, black, transparent)',
          }}
        ></div>

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border text-xs mono tracking-wider mb-7 shadow-sm" style={{ borderColor: '#1a1a2e1a' }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#d166e8' }}></span>
            RUN · CREATE · BUILD
          </div>

          <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-[1.02] mb-7">
            Experience a new way of learning.<br />
            <span className="brand-text">Like never before.</span>
          </h1>

          <div className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10 space-y-2.5" style={{ color: 'var(--ink-soft)' }}>
            <p>
              <span className="font-semibold" style={{ color: 'var(--ink)' }}>Run code right in your browser.</span>{' '}
              Any language, zero setup, results in seconds.
            </p>
            <p>
              <span className="font-semibold" style={{ color: 'var(--ink)' }}>Create tutorials on the fly.</span>{' '}
              Book-style tutorials in any language — with code that actually runs. Share them with friends.
            </p>
            <p>
              <span className="font-semibold" style={{ color: 'var(--ink)' }}>Build real projects.</span>{' '}
              With complete understanding of how every building block works.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <button onClick={goPay} className="px-7 py-3.5 font-semibold rounded-full brand-bg text-white hover:opacity-90 transition shadow-lg">
              Try for free →
            </button>
            <a href="#demo" className="px-7 py-3.5 font-semibold rounded-full bg-white border-2 hover:border-[#1a1a2e]/30 transition" style={{ borderColor: '#1a1a2e1a' }}>
              Watch the demo
            </a>
          </div>

          <p className="mt-6 text-sm" style={{ color: 'var(--ink-mute)' }}>7 days. Cancel anytime. No card games.</p>
        </div>
      </header>

      {/* ───── 3-CAPABILITY STRIP ───── */}
      <section className="pb-10" data-reveal>
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="card-soft p-5 flex items-start gap-4">
              <svg className="w-12 h-12 flex-shrink-0"><use href="#iso-cube" /></svg>
              <div>
                <p className="text-[10px] mono uppercase tracking-wider font-semibold mb-1" style={{ color: '#d166e8' }}>1 · Run</p>
                <p className="font-bold leading-tight mb-1">Any language. In your browser.</p>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--ink-soft)' }}>Python, Java, Go, Rust, C++, JS &amp; more. Zero install, zero setup.</p>
              </div>
            </div>

            <div className="card-soft p-5 flex items-start gap-4" style={{ background: 'linear-gradient(135deg, #fff5d1, #ffe1f5)' }}>
              <svg className="w-12 h-12 flex-shrink-0"><use href="#cube-smiley" /></svg>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-[10px] mono uppercase tracking-wider font-semibold" style={{ color: '#d166e8' }}>2 · Learn</p>
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded-full text-white tracking-wider" style={{ background: '#1a1a2e' }}>MOAT</span>
                </div>
                <p className="font-bold leading-tight mb-1">Tutorials that write themselves.</p>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--ink-soft)' }}>Ask "teach me X" — get a book-style tutorial with runnable code in 60s.</p>
              </div>
            </div>

            <div className="card-soft p-5 flex items-start gap-4">
              <svg className="w-12 h-12 flex-shrink-0"><use href="#iso-cube-soft" /></svg>
              <div>
                <p className="text-[10px] mono uppercase tracking-wider font-semibold mb-1" style={{ color: '#7c7cff' }}>3 · Build</p>
                <p className="font-bold leading-tight mb-1">Prompts → datasets → visualization.</p>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--ink-soft)' }}>Workflow takes one prompt all the way to a downloadable CSV and a chart.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── LIVE DEMO ───── */}
      <section id="demo" className="py-20" data-reveal>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs mono uppercase tracking-wider font-semibold mb-2" style={{ color: '#d166e8' }}>try below</p>
            <h2 className="text-3xl md:text-4xl font-black">Ask. Understand. Run.</h2>
            <p className="mt-3 text-sm" style={{ color: 'var(--ink-soft)' }}>A real interview question, explained and executed — right here on the page.</p>
          </div>

          <div className="card-soft overflow-hidden p-0">
            <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ background: '#fff5d166', borderColor: '#1a1a2e14' }}>
              <div className="flex gap-1.5">
                <span className="w-3 h-3 rounded-full" style={{ background: '#f87171' }}></span>
                <span className="w-3 h-3 rounded-full" style={{ background: '#facc15' }}></span>
                <span className="w-3 h-3 rounded-full" style={{ background: '#34d399' }}></span>
              </div>
              <span className="ml-3 text-xs mono" style={{ color: 'var(--ink-mute)' }}>cubemate — Chat</span>
            </div>

            <div className="grid md:grid-cols-2" style={{ background: 'white' }}>
              <div className="p-8 space-y-4 border-r" style={{ borderColor: '#1a1a2e14' }}>
                <div className="inline-flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full grid place-items-center text-[10px] font-bold" style={{ background: '#7c7cff26', color: '#7c7cff' }}>L</div>
                  <span className="text-xs mono uppercase tracking-wider" style={{ color: 'var(--ink-mute)' }}>you ask</span>
                </div>
                <p className="text-xl leading-snug font-medium">
                  How do I solve <span className="font-bold">Two Sum</span> in <span className="mono">O(n)</span>?
                </p>
                <p className="text-sm" style={{ color: 'var(--ink-soft)' }}>
                  Given the array <span className="mono px-1.5 py-0.5 rounded" style={{ background: '#1a1a2e0a' }}>[3, 8, 11, 7, 5]</span> and target <span className="mono px-1.5 py-0.5 rounded" style={{ background: '#1a1a2e0a' }}>10</span>, return the indices of the two numbers that add up to the target.
                </p>
              </div>

              <div className="p-8 space-y-4" style={{ background: '#fffaf0' }}>
                <div className="inline-flex items-center gap-2">
                  <svg className="w-7 h-7"><use href="#cube-smiley" /></svg>
                  <span className="text-xs mono uppercase tracking-wider" style={{ color: 'var(--ink-mute)' }}>cubemate</span>
                </div>
                <p className="text-sm leading-relaxed">
                  Walk the array once. For each <span className="mono px-1 rounded" style={{ background: '#7c7cff14', color: '#7c7cff' }}>n</span>, check if <span className="mono px-1 rounded" style={{ background: '#7c7cff14', color: '#7c7cff' }}>target − n</span> was already seen. If yes, you have the pair. One pass, <span className="mono px-1 rounded" style={{ background: '#7c7cff14', color: '#7c7cff' }}>O(n)</span> time.
                </p>
                <pre className="code-block overflow-x-auto">{`def two_sum(nums, target):
    seen = {}
    for i, n in enumerate(nums):
        if (target - n) in seen:
            return [seen[target - n], i]
        seen[n] = i

print(two_sum([3, 8, 11, 7, 5], 10))`}</pre>

                <div
                  className="overflow-hidden transition-all duration-500"
                  style={{
                    opacity: running === 'idle' ? 0 : 1,
                    maxHeight: running === 'idle' ? 0 : 200,
                  }}
                >
                  <div className="mt-3 border-l-4 pl-3 py-1" style={{ borderColor: '#34d399' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] mono uppercase tracking-wider font-bold" style={{ color: '#059669' }}>output</span>
                      {runtime !== null && (
                        <span className="text-[10px] mono" style={{ color: 'var(--ink-mute)' }}>· {runtime} ms</span>
                      )}
                    </div>
                    <pre className="mono text-xs leading-relaxed rounded p-2" style={{ background: '#d1fae566', color: 'var(--ink)' }}>
                      <div><span style={{ color: '#059669' }}>{'>'}</span>{' [0, 3]'}</div>
                      <div style={{ color: 'var(--ink-mute)' }}>{'# nums[0]=3 + nums[3]=7 = 10  ✓'}</div>
                    </pre>
                  </div>
                </div>

                <div className="flex gap-2 pt-1 items-center">
                  <button
                    onClick={runDemo}
                    disabled={running !== 'idle'}
                    className="px-4 py-2 text-xs font-semibold rounded-full text-white transition flex items-center gap-1.5 shadow-md disabled:opacity-80 disabled:cursor-not-allowed"
                    style={{ background: '#10b981' }}
                  >
                    <span className={running === 'running' ? 'home-spin' : ''}>
                      {running === 'idle' ? '▶' : running === 'running' ? '◐' : '✓'}
                    </span>
                    <span>
                      {running === 'idle' ? 'Run this code' : running === 'running' ? 'Running…' : 'Done'}
                    </span>
                  </button>
                  {running === 'idle' && (
                    <span className="text-xs ml-1" style={{ color: 'var(--ink-mute)' }}>
                      ← click to execute
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-sm mt-6" style={{ color: 'var(--ink-mute)' }}>
            Every code block runs in our cloud sandbox. No install. No setup.
          </p>
        </div>
      </section>

      {/* ───── THREE MODULES ───── */}
      <section id="product" className="py-24 relative" data-reveal>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs mono uppercase tracking-wider font-semibold mb-2" style={{ color: '#d166e8' }}>one workspace · three modules</p>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight">Pick the right tool for the job.</h2>
            <p className="mt-4" style={{ color: 'var(--ink-soft)' }}>Same login, same credits — three very different jobs done well.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            <div className="card-soft p-7">
              <div className="flex items-center justify-between mb-5">
                <svg className="w-12 h-12"><use href="#iso-cube" /></svg>
                <span className="text-[10px] mono" style={{ color: 'var(--ink-mute)' }}>/chat</span>
              </div>
              <h3 className="text-xl font-bold mb-1">Chat</h3>
              <p className="text-[11px] uppercase tracking-wider font-semibold mono mb-3" style={{ color: '#d166e8' }}>ask · understand · execute</p>
              <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--ink-soft)' }}>
                Programming Q&amp;A with explanation and a runnable sandbox in the same reply. Like ChatGPT, but the code actually runs.
              </p>
              <p className="text-[11px] uppercase tracking-wider mb-2 font-semibold" style={{ color: 'var(--ink-mute)' }}>use it for</p>
              <ul className="text-sm space-y-2 mb-5">
                <li className="flex gap-2"><span className="font-bold" style={{ color: '#d166e8' }}>→</span> <span className="italic">&quot;Why is my Python loop infinite?&quot;</span></li>
                <li className="flex gap-2"><span className="font-bold" style={{ color: '#d166e8' }}>→</span> <span className="italic">&quot;Explain pointers in C with a runnable example.&quot;</span></li>
                <li className="flex gap-2"><span className="font-bold" style={{ color: '#d166e8' }}>→</span> <span className="italic">Drop a CSV → &quot;Find average per subject.&quot;</span></li>
              </ul>
              <div className="pt-4 border-t text-xs" style={{ borderColor: '#1a1a2e14', color: 'var(--ink-soft)' }}>
                <span className="font-semibold" style={{ color: 'var(--ink)' }}>Best for</span> · debugging, concepts, exploring a new language
              </div>
            </div>

            <div className="card-soft p-7" style={{ background: 'linear-gradient(135deg, #fff8e8, #ffe8f5)', borderColor: '#d166e840' }}>
              <div className="flex items-center justify-between mb-5">
                <svg className="w-12 h-12"><use href="#cube-smiley" /></svg>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] mono" style={{ color: 'var(--ink-soft)' }}>/bookmate</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white tracking-wider" style={{ background: '#1a1a2e' }}>MOAT</span>
                </div>
              </div>
              <h3 className="text-xl font-bold mb-1">Bookmate</h3>
              <p className="text-[11px] uppercase tracking-wider font-semibold mono mb-3" style={{ color: '#d166e8' }}>learn a topic end-to-end</p>
              <p className="text-sm leading-relaxed mb-5">
                One prompt → a full book-style tutorial with runnable examples. From <span className="italic">&quot;teach me Rust&quot;</span> to a 12-chapter PDF in 90 seconds.
              </p>
              <p className="text-[11px] uppercase tracking-wider mb-2 font-semibold" style={{ color: 'var(--ink-soft)' }}>use it for</p>
              <ul className="text-sm space-y-2 mb-5">
                <li className="flex gap-2"><span className="font-bold" style={{ color: '#d166e8' }}>→</span> <span className="italic">&quot;Teach me Java OOP from scratch.&quot;</span></li>
                <li className="flex gap-2"><span className="font-bold" style={{ color: '#d166e8' }}>→</span> <span className="italic">&quot;Compare Python and Go side-by-side.&quot;</span></li>
                <li className="flex gap-2"><span className="font-bold" style={{ color: '#d166e8' }}>→</span> <span className="italic">&quot;DSA crash course for placement prep.&quot;</span></li>
              </ul>
              <div className="pt-4 border-t text-xs" style={{ borderColor: '#d166e84d' }}>
                <span className="font-semibold">Best for</span> · interview prep, new stack at work, topic mastery
              </div>
            </div>

            <div className="card-soft p-7">
              <div className="flex items-center justify-between mb-5">
                <svg className="w-12 h-12"><use href="#iso-cube-soft" /></svg>
                <span className="text-[10px] mono" style={{ color: 'var(--ink-mute)' }}>/workflow</span>
              </div>
              <h3 className="text-xl font-bold mb-1">Workflow</h3>
              <p className="text-[11px] uppercase tracking-wider font-semibold mono mb-3" style={{ color: '#7c7cff' }}>build real projects</p>
              <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--ink-soft)' }}>
                For projects that need real data. One prompt collects, processes, and visualizes a dataset you can download and submit.
              </p>
              <p className="text-[11px] uppercase tracking-wider mb-2 font-semibold" style={{ color: 'var(--ink-mute)' }}>use it for</p>
              <ul className="text-sm space-y-2 mb-5">
                <li className="flex gap-2"><span className="font-bold" style={{ color: '#7c7cff' }}>→</span> <span className="italic">&quot;Top 100 CS colleges in India with fees.&quot;</span></li>
                <li className="flex gap-2"><span className="font-bold" style={{ color: '#7c7cff' }}>→</span> <span className="italic">&quot;Scrape data-analyst job postings.&quot;</span></li>
                <li className="flex gap-2"><span className="font-bold" style={{ color: '#7c7cff' }}>→</span> <span className="italic">&quot;Final-year project dataset on EV adoption.&quot;</span></li>
              </ul>
              <div className="pt-4 border-t text-xs" style={{ borderColor: '#1a1a2e14', color: 'var(--ink-soft)' }}>
                <span className="font-semibold" style={{ color: 'var(--ink)' }}>Best for</span> · first real projects, portfolio work, capstones
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── OUTCOMES ───── */}
      <section id="outcomes" className="py-24 relative" data-reveal>
        <svg className="absolute top-12 left-[5%] w-12 h-12 opacity-50 float-2"><use href="#iso-cube-soft" /></svg>
        <svg className="absolute bottom-32 right-[8%] w-14 h-14 opacity-60 float-3"><use href="#iso-cube" /></svg>

        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs mono uppercase tracking-wider font-semibold mb-2" style={{ color: '#d166e8' }}>why learners switch</p>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight">Built for outcomes, not just answers.</h2>
            <p className="mt-4" style={{ color: 'var(--ink-soft)' }}>Every feature ladders up to a result you can show — a finished project, a cracked interview, a shipped dataset.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            <div className="tape rounded-2xl p-7">
              <div className="flex gap-0.5 mb-3" aria-label="5 out of 5 stars">
                {[0,1,2,3,4].map(i => (
                  <svg key={i} className="w-4 h-4" fill="#f59e0b" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                ))}
              </div>
              <p className="text-[15px] leading-relaxed mb-5">
                &quot;I had two weeks to prep DSA for placement. Generated a tutorial on every topic, ran every example, walked into the interview ready.&quot;
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-dashed" style={{ borderColor: '#1a1a2e26' }}>
                <div className="w-9 h-9 rounded-full brand-bg grid place-items-center text-sm font-bold text-white">P</div>
                <div>
                  <p className="text-sm font-semibold">Priya · Final-year CS</p>
                  <p className="text-xs" style={{ color: 'var(--ink-mute)' }}>Cracking interviews</p>
                </div>
              </div>
            </div>

            <div className="tape rounded-2xl p-7">
              <div className="flex gap-0.5 mb-3" aria-label="5 out of 5 stars">
                {[0,1,2,3,4].map(i => (
                  <svg key={i} className="w-4 h-4" fill="#f59e0b" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                ))}
              </div>
              <p className="text-[15px] leading-relaxed mb-5">
                &quot;Switched from teaching to tech at 32. Bookmate taught me Python in two weeks, Workflow let me build a portfolio dataset. Landed my first dev role.&quot;
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-dashed" style={{ borderColor: '#1a1a2e26' }}>
                <div className="w-9 h-9 rounded-full brand-bg grid place-items-center text-sm font-bold text-white">M</div>
                <div>
                  <p className="text-sm font-semibold">Maya · Career switcher</p>
                  <p className="text-xs" style={{ color: 'var(--ink-mute)' }}>Teacher → data engineer</p>
                </div>
              </div>
            </div>

            <div className="tape rounded-2xl p-7">
              <div className="flex gap-0.5 mb-3" aria-label="5 out of 5 stars">
                {[0,1,2,3,4].map(i => (
                  <svg key={i} className="w-4 h-4" fill="#f59e0b" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                ))}
              </div>
              <p className="text-[15px] leading-relaxed mb-5">
                &quot;I write Java at work but had to pick up Go for a new service. Side-by-side Bookmate tutorial in 90 minutes — shipped my first Go PR the same week.&quot;
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-dashed" style={{ borderColor: '#1a1a2e26' }}>
                <div className="w-9 h-9 rounded-full brand-bg grid place-items-center text-sm font-bold text-white">R</div>
                <div>
                  <p className="text-sm font-semibold">Rohit · Backend engineer</p>
                  <p className="text-xs" style={{ color: 'var(--ink-mute)' }}>Learning at work</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              ['12k+', 'tutorials generated'],
              ['8', 'languages supported'],
              ['3 min', 'avg time to a dataset'],
              ['10k+', 'active learners'],
            ].map(([num, label]) => (
              <div key={label} className="text-center">
                <p className="text-4xl font-black brand-text">{num}</p>
                <p className="text-xs mt-1 uppercase tracking-wider font-semibold" style={{ color: 'var(--ink-mute)' }}>{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── HOW IT WORKS ───── */}
      <section id="how" className="py-24" data-reveal>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs mono uppercase tracking-wider font-semibold mb-2" style={{ color: '#d166e8' }}>how it works</p>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight">Three steps. No setup.</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-9 left-[20%] right-[20%] h-px border-t-2 border-dashed" style={{ borderColor: '#1a1a2e26' }}></div>
            {[
              ['1', 'Ask', 'Type what you need in plain English — a question, a topic, a dataset.'],
              ['2', 'Generate', 'Cubemate picks the right module — chat, tutorial, or workflow — and gets to work.'],
              ['3', 'Run or download', 'Execute code in the cloud, save the tutorial, or download your CSV. Yours to keep.'],
            ].map(([n, title, desc]) => (
              <div key={n} className="text-center relative">
                <div className="num-cube w-16 h-16 rounded-2xl grid place-items-center text-2xl mx-auto mb-5">{n}</div>
                <h3 className="font-bold mb-2 text-lg">{title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--ink-soft)' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── FINAL CTA ───── */}
      <section className="py-24" data-reveal>
        <div className="max-w-4xl mx-auto px-6">
          <div className="relative rounded-3xl overflow-hidden p-12 md:p-16 text-center text-white" style={{ background: 'var(--brand-gradient)' }}>
            <svg className="absolute -top-8 -right-6 w-40 h-40 opacity-40"><use href="#cube-smiley" /></svg>
            <svg className="absolute -bottom-10 -left-6 w-32 h-32 opacity-30 float-1"><use href="#iso-cube" /></svg>

            <div className="relative">
              <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-5">
                Ready to ship, not just learn?
              </h2>
              <p className="text-white/90 text-lg mb-8 max-w-xl mx-auto">
                Try every module for 7 days. Cancel anytime. No card games.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={goPay} className="px-7 py-3.5 font-bold rounded-full bg-white hover:bg-[#fefae8] transition" style={{ color: 'var(--ink)' }}>
                  Try for free →
                </button>
                <button onClick={goPricing} className="px-7 py-3.5 font-semibold rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition">
                  See pricing
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───── FOOTER ───── */}
      <footer className="border-t py-10" style={{ borderColor: '#1a1a2e14' }}>
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm" style={{ color: 'var(--ink-mute)' }}>
          <div className="flex items-center gap-2.5">
            <svg className="w-7 h-7"><use href="#cube-smiley" /></svg>
            <span className="font-semibold" style={{ color: 'var(--ink)' }}>Cubemate</span>
            <span>· Built for learners who ship</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <button onClick={goPricing} className="hover:text-[#1a1a2e] transition">Pricing</button>
            <a href="/privacy" className="hover:text-[#1a1a2e] transition">Privacy</a>
            <a href="/terms" className="hover:text-[#1a1a2e] transition">Terms</a>
            <a href="https://www.linkedin.com/company/103316827/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-[#0a66c2] transition">
              <LinkedInIcon /> CorestratAI Solutions
            </a>
            <a href="https://www.instagram.com/corestratai/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-[#E4405F] transition">
              <InstagramIcon /> CorestratAI Solutions
            </a>
            <a href="https://www.instagram.com/cubemate_ai/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-[#E4405F] transition">
              <InstagramIcon /> Cubemate AI
            </a>
          </div>
        </div>
        <p className="text-center text-xs mt-6" style={{ color: 'var(--ink-mute)' }}>© 2026 Cubemate. Made in India.</p>
      </footer>
    </div>
  );
};

export default HomePage;
